/**
 * Sidebar order — each person can drag the menu into their own order.
 *
 * The order is a list of tab ids kept in this browser's localStorage, one
 * list per signed-in person (keyed on their directory id), so two people
 * sharing a PC each keep theirs. It is a convenience only: an unreadable or
 * missing list just means the default order.
 */

const STORAGE_PREFIX = 'omni:sidebarOrder:';

function storageKey(userId) {
  return STORAGE_PREFIX + (userId || 'anonymous');
}

/** @param {string | undefined} userId @returns {string[] | null} */
export function readSidebarOrder(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : null;
  } catch {
    return null;
  }
}

/** @param {string | undefined} userId @param {string[] | null} order */
export function writeSidebarOrder(userId, order) {
  try {
    if (order) localStorage.setItem(storageKey(userId), JSON.stringify(order));
    else localStorage.removeItem(storageKey(userId));
  } catch {
    /* ignore (private mode etc.) */
  }
}

/**
 * Put the tabs into the saved order. Ids that no longer exist are ignored; a
 * tab added since the order was saved goes in after the tab that precedes it
 * in the default order, so a new feature appears where it normally would
 * rather than at the bottom.
 * @template {{ tabId: string }} T
 * @param {T[]} tabs   default order
 * @param {string[] | null} order
 * @returns {T[]}
 */
export function applySidebarOrder(tabs, order) {
  if (!order || order.length === 0) return tabs;
  const byId = new Map(tabs.map((t) => [t.tabId, t]));
  const result = [];
  const placed = new Set();
  for (const id of order) {
    const tab = byId.get(id);
    if (tab && !placed.has(id)) {
      result.push(tab);
      placed.add(id);
    }
  }
  tabs.forEach((tab, index) => {
    if (placed.has(tab.tabId)) return;
    let insertAt = 0;
    for (let i = index - 1; i >= 0; i -= 1) {
      const at = result.indexOf(tabs[i]);
      if (at !== -1) {
        insertAt = at + 1;
        break;
      }
    }
    result.splice(insertAt, 0, tab);
    placed.add(tab.tabId);
  });
  return result;
}

/**
 * Move one tab before or after another. Works on the FULL ordered list, so
 * tabs this person cannot see keep their places.
 * @param {string[]} ids
 * @param {string} movingId
 * @param {string} targetId
 * @param {'before' | 'after'} position
 * @returns {string[]}
 */
export function moveSidebarTab(ids, movingId, targetId, position) {
  if (movingId === targetId || !ids.includes(movingId) || !ids.includes(targetId)) return ids;
  const without = ids.filter((id) => id !== movingId);
  const targetAt = without.indexOf(targetId);
  without.splice(position === 'after' ? targetAt + 1 : targetAt, 0, movingId);
  return without;
}

/**
 * Split the (already ordered, already permission-filtered) tabs into the
 * sidebar's sections: first the tabs with no group (Home), then one section
 * per group in the groups' own order. A group with no tabs left is dropped.
 * A tab naming an unknown group goes with the ungrouped tabs rather than
 * disappearing.
 * @template {{ tabId: string, group?: string | null }} T
 * @param {T[]} tabs
 * @param {{ id: string, label: string }[]} groups
 * @returns {{ id: string | null, label: string | null, tabs: T[] }[]}
 */
export function groupSidebarTabs(tabs, groups) {
  const known = new Set(groups.map((g) => g.id));
  const top = tabs.filter((t) => !t.group || !known.has(t.group));
  const sections = [];
  if (top.length) sections.push({ id: null, label: null, tabs: top });
  for (const g of groups) {
    const inGroup = tabs.filter((t) => t.group === g.id);
    if (inGroup.length) sections.push({ id: g.id, label: g.label, tabs: inGroup });
  }
  return sections;
}

const CLOSED_PREFIX = 'omni:sidebarClosedGroups:';

/** Group ids this person has closed. @param {string | undefined} userId @returns {string[]} */
export function readClosedGroups(userId) {
  try {
    const raw = localStorage.getItem(CLOSED_PREFIX + (userId || 'anonymous'));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string | undefined} userId @param {string[]} groupIds */
export function writeClosedGroups(userId, groupIds) {
  try {
    const key = CLOSED_PREFIX + (userId || 'anonymous');
    if (groupIds.length) localStorage.setItem(key, JSON.stringify(groupIds));
    else localStorage.removeItem(key);
  } catch {
    /* ignore (private mode etc.) */
  }
}
